import React, { lazy, Suspense, useEffect, useState } from "react";
import { Tab, Tabs } from "@heroui/tabs";
import { PawPrint, Tractor } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";

import "driver.js/dist/driver.css";
import { useQueryClient } from "@tanstack/react-query";

import { Farmer } from "../Farmers/FarmerSVG.tsx";

import { ErrorFallback, SuspenseFallback } from "@/lib/Utils.Components.tsx";
import { useRealTimeListeners } from "@/hooks/useRealTimeListeners.tsx";
import { adminKeys } from "@/config/Query.Keys.ts";
import {
  fetchAllAnimals,
  fetchAllFarmers,
  fetchAllFarms,
  fetchAllPhenotype,
  fetchAllSupervisors,
} from "@/features/Admin/Admin.api.ts";
import { HeroBackground } from "@/features/ui/Background.tsx";

type AdminTabKey = "Animals" | "SupervisorFarms" | "SupervisorFarmers";

const Animals = lazy(() => import("../../pages/Dashboard.tsx"));
const SupervisorFarm = lazy(() => import("./Supervisor.Farms.tsx"));
const SupervisorFarmers = lazy(() => import("./Supervisor.Farmers.tsx"));

function SupervisorDashboard() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<AdminTabKey>("Animals");

  useRealTimeListeners();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: adminKeys.allAnimals(),
      queryFn: fetchAllAnimals,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allFarmers(),
      queryFn: fetchAllFarmers,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allFarms(),
      queryFn: fetchAllFarms,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allSupervisors(),
      queryFn: fetchAllSupervisors,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.phenotype(),
      queryFn: fetchAllPhenotype,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allOfficials(),
      // queryFn: fetchAllPhenotype,
    });
  }, [queryClient]);

  return (
    <>
      <div className={"w-full h-full overflow-x-hidden py-2 z-10"}>
        <HeroBackground />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Tabs
            key={"tabs"}
            aria-label={"Tabs"}
            className={"max-w-screen"}
            classNames={{
              base: " z-50",
              tabList:
                " mx-1 border-gray-2 z-50 " +
                " overflow-x-auto dark:border-2" +
                " dark:border-stone-800",
              tabContent:
                "text-[#015354ff] z-50" +
                " group-data-[selected=true]:text-white ",
              cursor: "bg-[#008c8fff]",
            }}
            selectedKey={selected}
            onSelectionChange={(key) => setSelected(key as AdminTabKey)}
            variant={"bordered"}
          >
            <Tab
              key={"Animals"}
              id={"Animals"}
              title={
                <div className="flex items-center space-x-2">
                  <PawPrint size={"22px"} />
                  <span>Animals</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Suspense fallback={<SuspenseFallback />}>
                  <Animals />
                </Suspense>
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"SupervisorFarms"}
              id={"SupervisorFarms"}
              title={
                <div className="flex items-center space-x-2">
                  <Tractor size={"22px"} />
                  <span>Farms</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Suspense fallback={<SuspenseFallback />}>
                  <SupervisorFarm />
                </Suspense>
              </ErrorBoundary>
            </Tab>

            <Tab
              key={"SupervisorFarmers"}
              id={`SupervisorFarmers`}
              title={
                <div className="flex items-center space-x-2">
                  <Farmer />
                  <span>Farmers</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Suspense fallback={<SuspenseFallback />}>
                  <SupervisorFarmers />
                </Suspense>
              </ErrorBoundary>
            </Tab>
          </Tabs>
        </ErrorBoundary>
      </div>
    </>
  );
}

const MemoizedSupervisorDashboard = React.memo(SupervisorDashboard);

export default MemoizedSupervisorDashboard;
